import { useCallback, useMemo, useRef } from "react"
import { useSelector } from "react-redux"

import type { SpanDatum } from "types/spans.types"
import type { ValueSummaryRecord } from "types/telemetries.types"

import {
  LIMITING_SPAN_MEASUREMENT,
  limitingSpanFrequencyView,
} from "helpers/config/dashboards/powerline/defs"
import {
  buildSpanDataFromCounts,
  type CountsByField,
} from "helpers/utils/limitingSpanData"
import {
  usePowerlineTelemetryQuery,
  usePowerlineTelemetryValueSummaryQuery,
} from "features/api"
import { selectCurrentOrgId } from "features/store/orgSlice"
import useDateRange from "helpers/hooks/useDateRange"

interface UseLimitingSpanFrequencyProps {
  powerlineId: number
  // The line's spans in route order — what both views are laid out along.
  orderedSpanIds: number[]
}

/**
 * The spans the map singles out: what each method reports as limiting now, and
 * what each method limited on most often over the window.
 */
export interface LimitingSpanHighlights {
  currentAarSpanId: number | null
  currentDlrSpanId: number | null
  // ISO, or null when we don't know when it was last reported.
  currentAarTime: string | null
  currentDlrTime: string | null
  peakAarSpanId: number | null
  peakDlrSpanId: number | null
}

interface UseLimitingSpanFrequencyResult {
  spanData: SpanDatum[]
  maxPct: number
  highlights: LimitingSpanHighlights
  // Returned rather than re-read from `useDateRange`, so the card prints the
  // range the numbers actually came from.
  range: { fromDate: string; toDate: string } | null
  // Any query in flight, including a refetch over data already on screen.
  isLoading: boolean
  /**
   * At least one method has given no answer yet — neither counts nor a failure.
   * Not `isLoading`, which is also true while a poll refreshes rows on screen;
   * and a new date range is a new cache entry, so `undefined` there means "not
   * yet" rather than "settled, no counts".
   */
  isPending: boolean
  // Nothing was asked: no line, no org, or a range mid-change. Not an answer
  // either, so the card holds rather than reading it as "no counts".
  isSkipped: boolean
  error: unknown | null
  // One method loaded and the other didn't: incomplete, not absent.
  hasPartialError: boolean
  // Per method, not one flag: a failed lane drawn as zeros would claim that
  // method never limited the line, which is not what we know.
  methodErrors: { aar: boolean; dlr: boolean }
  // Whether there is a limiting-span layer to draw. Only means that once
  // `isPending` is false; before that it just means "not yet".
  hasCounts: boolean
  refetch: () => void
}

const { series } = limitingSpanFrequencyView.properties

const AAR_FIELD = series.find((s) => s.dataKey === "aar")?.field
const DLR_FIELD = series.find((s) => s.dataKey === "dlr")?.field

// Folds `only_value_summary` rows into `{ field: { spanId: count } }`.
const collectCounts = (
  target: CountsByField,
  rows: ValueSummaryRecord[] | undefined,
): CountsByField => {
  for (const row of rows ?? []) {
    if (!row._field || row.name === null || row.name === undefined) continue
    if (!target[row._field]) target[row._field] = {}
    target[row._field][String(row.name)] = Number(row.count)
  }
  return target
}

const hasCounts = (rows: ValueSummaryRecord[] | undefined): boolean =>
  (rows ?? []).some((row) => Number(row.count) > 0)

/**
 * At least one method has yet to answer, with counts or with a failure. Skipped
 * is settled: nothing will answer, so the card would sit on a skeleton for good.
 */
export const getIsFrequencyPending = (
  skip: boolean,
  methods: { rows: ValueSummaryRecord[] | undefined; error: unknown }[],
): boolean => !skip && methods.some(({ rows, error }) => rows === undefined && !error)

/**
 * The rows a query last settled on. RTK drops `currentData` the moment the
 * arguments change, so a new date range would empty the panel while it loads.
 * Cleared when the line changes, so one line never draws another's counts, and
 * when a failure leaves the current arguments with nothing of their own, so a
 * method never keeps counting the rows of the range it succeeded on — the hold
 * is for "not yet", not for "not anymore". Rows the current arguments did
 * return still win: RTK keeps them through a rejection, and they are this
 * range's, so a refetch that fails is no reason to drop them.
 */
export const useHeldRows = <T,>(
  rows: T | undefined,
  powerlineId: number,
  error: unknown,
): T | undefined => {
  const held = useRef<{ powerlineId: number; rows: T | undefined }>({
    powerlineId,
    rows: undefined,
  })
  if (held.current.powerlineId !== powerlineId) {
    held.current = { powerlineId, rows: undefined }
  }
  if (rows !== undefined) held.current.rows = rows
  else if (error) held.current.rows = undefined
  return held.current.rows
}

/**
 * The frequency dataset the map and the ribbon share, over the dashboard's
 * powerline date range.
 *
 * Counts come from `only_value_summary`, so the backend tallies and we never
 * fetch the series. AAR and DLR go separately to fail independently. A third
 * `last` request gives the currently-limiting span, which the summary rows
 * can't: they carry no `_time`. The map's alert icon and the ribbon's arrow
 * both read it, so they always name the same span.
 */
export const useLimitingSpanFrequency = ({
  powerlineId,
  orderedSpanIds,
}: UseLimitingSpanFrequencyProps): UseLimitingSpanFrequencyResult => {
  const orgId = useSelector(selectCurrentOrgId)
  const { getDateRangeTools } = useDateRange()
  const { dateRange, isDateRangeValid } = useMemo(
    () => getDateRangeTools("powerline"),
    [getDateRangeTools],
  )

  const skip = !powerlineId || orgId === undefined || !isDateRangeValid || !dateRange

  const baseParams = useMemo(
    () => ({
      from_date: dateRange?.fromDate,
      to_date: dateRange?.toDate,
      org_id: Number(orgId),
      measurement: LIMITING_SPAN_MEASUREMENT,
    }),
    [dateRange?.fromDate, dateRange?.toDate, orgId],
  )

  // One request per method, so InfluxDB scans the two fields in parallel.
  const {
    currentData: aarRows,
    isFetching: aarFetching,
    error: aarError,
    refetch: refetchAar,
  } = usePowerlineTelemetryValueSummaryQuery(
    { id: powerlineId, params: { ...baseParams, field: AAR_FIELD ? [AAR_FIELD] : [] } },
    { skip: skip || !AAR_FIELD },
  )

  const {
    currentData: dlrRows,
    isFetching: dlrFetching,
    error: dlrError,
    refetch: refetchDlr,
  } = usePowerlineTelemetryValueSummaryQuery(
    { id: powerlineId, params: { ...baseParams, field: DLR_FIELD ? [DLR_FIELD] : [] } },
    { skip: skip || !DLR_FIELD },
  )

  // Currently limiting span per method. Both fields in one request, since
  // `last` already reduces each to a single row.
  const {
    currentData: latestRows,
    isFetching: latestFetching,
    error: latestError,
    refetch: refetchLatest,
  } = usePowerlineTelemetryQuery(
    {
      id: powerlineId,
      params: {
        ...baseParams,
        field: [AAR_FIELD, DLR_FIELD].filter((field): field is string => !!field),
        method: ["last"],
        group: ["_field"],
      },
    },
    { skip },
  )

  const aarSummary = useHeldRows(aarRows, powerlineId, aarError)
  const dlrSummary = useHeldRows(dlrRows, powerlineId, dlrError)
  const latest = useHeldRows(latestRows, powerlineId, latestError)

  const { spanData, maxPct } = useMemo(() => {
    const countsByField = collectCounts(collectCounts({}, aarSummary), dlrSummary)
    return buildSpanDataFromCounts(countsByField, series, orderedSpanIds)
  }, [aarSummary, dlrSummary, orderedSpanIds])

  const highlights = useMemo<LimitingSpanHighlights>(() => {
    // `last` grouped by `_field` should give one row per method; compare `_time`
    // anyway so a stray extra row can't beat the newest.
    const latestByField: Record<string, { time: string; spanId: number }> = {}
    for (const record of latest ?? []) {
      const field = typeof record._field === "string" ? record._field : ""
      const time = typeof record._time === "string" ? record._time : ""
      const spanId = Number(record._value)
      if (!field || !spanId) continue
      if (!latestByField[field] || time > latestByField[field].time) {
        latestByField[field] = { time, spanId }
      }
    }

    // Peak span per method. On a tie the first one wins.
    let peakAar: SpanDatum | null = null
    let peakDlr: SpanDatum | null = null
    for (const d of spanData) {
      if (d.aar.count > 0 && (!peakAar || d.aar.count > peakAar.aar.count)) peakAar = d
      if (d.dlr.count > 0 && (!peakDlr || d.dlr.count > peakDlr.dlr.count)) peakDlr = d
    }

    return {
      currentAarSpanId: AAR_FIELD ? (latestByField[AAR_FIELD]?.spanId ?? null) : null,
      currentDlrSpanId: DLR_FIELD ? (latestByField[DLR_FIELD]?.spanId ?? null) : null,
      currentAarTime: AAR_FIELD ? latestByField[AAR_FIELD]?.time || null : null,
      currentDlrTime: DLR_FIELD ? latestByField[DLR_FIELD]?.time || null : null,
      peakAarSpanId: peakAar?.spanId ?? null,
      peakDlrSpanId: peakDlr?.spanId ?? null,
    }
  }, [latest, spanData])

  // Both ends or nothing: a half-open range isn't printable, and the queries
  // are skipped in that state anyway.
  const range = useMemo(
    () =>
      dateRange?.fromDate && dateRange?.toDate
        ? { fromDate: dateRange.fromDate, toDate: dateRange.toDate }
        : null,
    [dateRange?.fromDate, dateRange?.toDate],
  )

  const refetch = useCallback(() => {
    if (skip) return
    void refetchAar()
    void refetchDlr()
    void refetchLatest()
  }, [skip, refetchAar, refetchDlr, refetchLatest])

  const isLoading = aarFetching || dlrFetching || latestFetching
  const error = aarError ?? dlrError ?? latestError ?? null
  // One method failing while the other has counts still leaves a usable ribbon.
  const hasPartialError =
    Boolean(error) && (hasCounts(aarSummary) || hasCounts(dlrSummary))

  // Failed and with nothing to draw. A method that still has this range's rows
  // keeps its lane; the retry chip is what says the last request failed.
  const methodErrors = useMemo(
    () => ({
      aar: Boolean(aarError) && aarSummary === undefined,
      dlr: Boolean(dlrError) && dlrSummary === undefined,
    }),
    [aarError, dlrError, aarSummary, dlrSummary],
  )

  const isPending = getIsFrequencyPending(skip, [
    { rows: aarSummary, error: aarError },
    { rows: dlrSummary, error: dlrError },
  ])

  return {
    spanData,
    maxPct,
    highlights,
    range,
    isLoading,
    isPending,
    isSkipped: skip,
    error,
    hasPartialError,
    methodErrors,
    // Not gated on `isLoading`: RTK keeps the rows for these arguments through
    // a refetch, so the counts hold. Gating it would flip this on every poll.
    hasCounts: hasCounts(aarSummary) || hasCounts(dlrSummary),
    refetch,
  }
}

export default useLimitingSpanFrequency
